// @migration-in-progress
/**
 * PDF Core Utilities for FleetSkipper
 * 
 * Shared PDF generation utilities, formatting, and helper functions
 * Used by all specialized PDF export modules
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Extends jsPDF prototype with autoTable method
import toast from 'react-hot-toast';
import { debug } from '../logging';

/**
 * Standard PDF configuration and constants
 * Updated to maritime professional standards (8pt body, 15mm margins)
 */
export const PDF_CONFIG = {
  // Page setup
  format: 'a4',
  orientation: 'portrait',
  unit: 'mm',

  // Layout constants - Maritime standard (compact)
  margin: 15,        // Reduced from 20mm
  lineHeight: 4.5,   // Reduced from 6mm (tighter spacing)
  sectionSpacing: 6, // Reduced from 10mm

  // Font settings - Maritime professional standard
  fonts: {
    title: { size: 14, style: 'bold' },      // Reduced from 18
    heading: { size: 11, style: 'bold' },    // Reduced from 14
    subheading: { size: 10, style: 'bold' }, // Reduced from 12
    body: { size: 8, style: 'normal' },      // Reduced from 10 - MARITIME STANDARD
    small: { size: 7, style: 'normal' },     // Reduced from 8
    footer: { size: 7, style: 'italic' },    // Reduced from 8
    table: { size: 8, style: 'normal' }      // NEW: Table cell text
  }
};

/**
 * Create a new PDF document with standard setup
 * @returns {jsPDF} Configured PDF document
 */
export function createPDF() {
  const pdf = new jsPDF(PDF_CONFIG.orientation, PDF_CONFIG.unit, PDF_CONFIG.format);
  return pdf;
}

/**
 * Get page dimensions for the current PDF
 * @param {jsPDF} pdf - PDF document
 * @returns {Object} Page dimensions and calculated content area
 */
export function getPageDimensions(pdf) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - (PDF_CONFIG.margin * 2);
  
  return {
    pageWidth,
    pageHeight,
    contentWidth,
    margin: PDF_CONFIG.margin
  };
}

/**
 * Set font for PDF text
 * @param {jsPDF} pdf - PDF document
 * @param {string} type - Font type from PDF_CONFIG.fonts
 */
export function setFont(pdf, type = 'body') {
  const font = PDF_CONFIG.fonts[type] || PDF_CONFIG.fonts.body;
  pdf.setFontSize(font.size);
  pdf.setFont('helvetica', font.style);
}

/**
 * Add page break if needed
 * @param {jsPDF} pdf - PDF document
 * @param {number} currentY - Current Y position
 * @param {Function} addHeaderFunc - Function to add header on new page
 * @param {number} requiredSpace - Space needed for next content
 * @returns {Object} { currentY, pageAdded }
 */
export function checkPageBreak(pdf, currentY, addHeaderFunc, requiredSpace = 20) {
  const { pageHeight, margin } = getPageDimensions(pdf);
  
  if (currentY + requiredSpace > pageHeight - margin) {
    pdf.addPage();
    currentY = margin;
    if (addHeaderFunc) {
      currentY = addHeaderFunc(pdf, currentY);
    }
    return { currentY, pageAdded: true };
  }
  
  return { currentY, pageAdded: false };
}

/**
 * Add standard report header
 * @param {jsPDF} pdf - PDF document
 * @param {number} currentY - Current Y position
 * @param {Object} options - Header options
 * @returns {number} New Y position after header
 */
export function addReportHeader(pdf, currentY, options = {}) {
  const {
    title = 'FleetSkipper Report',
    vesselName = 'Unknown Vessel',
    category = 'Unknown Category',
    filters = {}
  } = options;
  
  const { margin } = getPageDimensions(pdf);
  
  // Title
  setFont(pdf, 'title');
  pdf.text(title, margin, currentY);
  currentY += 10;

  // Vessel Information
  setFont(pdf, 'body');
  pdf.text(`Vessel: ${vesselName} (${category})`, margin, currentY);
  currentY += PDF_CONFIG.lineHeight;

  // Report metadata
  const reportDate = new Date().toLocaleDateString('en-GB');
  const reportTime = new Date().toLocaleTimeString('en-GB', { hour12: false });
  pdf.text(`Generated: ${reportDate} ${reportTime}`, margin, currentY);
  currentY += PDF_CONFIG.lineHeight;

  // Filter information
  if (Object.keys(filters).length > 0) {
    pdf.text('Filters Applied:', margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
    
    if (filters.module) {
      pdf.text(`  • Module: ${filters.module}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }
    if (filters.action) {
      pdf.text(`  • Action: ${filters.action}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }
    if (filters.fromDate) {
      pdf.text(`  • From: ${new Date(filters.fromDate).toLocaleDateString('en-GB')}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }
    if (filters.toDate) {
      pdf.text(`  • To: ${new Date(filters.toDate).toLocaleDateString('en-GB')}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }
  }

  // Add separator line
  currentY += PDF_CONFIG.sectionSpacing;
  const { pageWidth } = getPageDimensions(pdf);
  pdf.setLineWidth(0.5);
  pdf.line(margin, currentY, pageWidth - margin, currentY);
  currentY += PDF_CONFIG.sectionSpacing;

  return currentY;
}

/**
 * Add standard report footer
 * @param {jsPDF} pdf - PDF document
 * @param {Object} options - Footer options
 */
export function addReportFooter(pdf, options = {}) {
  const { pageWidth, pageHeight, margin } = getPageDimensions(pdf);
  const { customText = '' } = options;
  
  let footerY = pageHeight - margin - 15;
  
  // Separator line
  pdf.setLineWidth(0.5);
  pdf.line(margin, footerY, pageWidth - margin, footerY);
  footerY += 5;
  
  // Footer text
  setFont(pdf, 'footer');
  const reportDate = new Date().toLocaleDateString('en-GB');
  const reportTime = new Date().toLocaleTimeString('en-GB', { hour12: false });
  
  pdf.text('Generated by FleetSkipper - WBC3 Compliant Maritime Software', margin, footerY);
  pdf.text(`Report generated: ${reportDate} ${reportTime}`, pageWidth - margin - 50, footerY);
  
  if (customText) {
    footerY += PDF_CONFIG.lineHeight;
    pdf.text(customText, margin, footerY);
  }
}

/**
 * Add a section heading with consistent formatting
 * @param {jsPDF} pdf - PDF document
 * @param {number} currentY - Current Y position
 * @param {string} title - Section title
 * @param {string} type - Heading type ('heading' or 'subheading')
 * @returns {number} New Y position after heading
 */
export function addSectionHeading(pdf, currentY, title, type = 'heading') {
  setFont(pdf, type);
  const { margin } = getPageDimensions(pdf);
  pdf.text(title, margin, currentY);
  currentY += PDF_CONFIG.lineHeight + 3;
  return currentY;
}

/**
 * Add wrapped text with automatic line breaking
 * @param {jsPDF} pdf - PDF document
 * @param {number} currentY - Current Y position
 * @param {string} text - Text to add
 * @param {Object} options - Text options
 * @returns {number} New Y position after text
 */
export function addWrappedText(pdf, currentY, text, options = {}) {
  const {
    fontType = 'body',
    indent = 0,
    maxWidth = null
  } = options;
  
  const { margin, contentWidth } = getPageDimensions(pdf);
  const textWidth = maxWidth || (contentWidth - indent);
  
  setFont(pdf, fontType);
  const lines = pdf.splitTextToSize(text, textWidth);
  pdf.text(lines, margin + indent, currentY);
  currentY += lines.length * PDF_CONFIG.lineHeight;
  
  return currentY;
}

/**
 * Format date for PDF display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  if (!date) return 'Not specified';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  return d.toLocaleDateString('en-GB');
}

/**
 * Format date and time for PDF display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(date) {
  if (!date) return 'Not specified';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour12: false })}`;
}

/**
 * Save PDF with standard filename generation
 * @param {jsPDF} pdf - PDF document
 * @param {string} prefix - Filename prefix
 * @param {string|Date} date - Date for filename
 * @param {string} suffix - Additional suffix
 * @returns {string} Generated filename
 */
export function savePDF(pdf, prefix, date = new Date(), suffix = '') {
  const dateStr = new Date(date).toISOString().split('T')[0];
  const filename = `${prefix}_${dateStr}${suffix ? '_' + suffix : ''}.pdf`;

  // Detect mobile devices - open in new tab for better UX (user can close tab to return)
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  if (isMobile) {
    // Mobile: Open PDF in new tab with blob URL
    // User can close the tab to return to the app
    const blob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    // Clean up blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);

    // Show toast with instructions
    toast.success(
      isIOS
        ? 'PDF opened in new tab. Close the tab to return.'
        : 'PDF opened. Use back button or close tab to return.',
      { duration: 4000 }
    );

    debug.log('PDF opened in new tab (mobile)', { filename, prefix, date, suffix });
  } else {
    // Desktop: Use jsPDF's save method for direct download
    pdf.save(filename);
    toast.success(`PDF downloaded: ${filename}`, { duration: 3000 });
    debug.log('PDF saved', { filename, prefix, date, suffix });
  }

  return filename;
}

/**
 * Handle PDF export errors consistently
 * @param {Error} error - Error object
 * @param {string} operation - Description of the operation that failed
 * @param {string} userMessage - User-friendly error message
 */
export function handlePDFError(error, operation, userMessage = 'Failed to generate PDF report. Please try again.') {
  debug.error(`PDF export failed - ${operation}:`, error);
  alert(userMessage);
}

/**
 * Add MCA compliance metadata to PDF
 * Displays vessel Official Number, retention period with "Keep Until" date,
 * and regulatory reference for UK-registered vessels
 *
 * @param {jsPDF} pdf - PDF document instance
 * @param {Object} vessel - Vessel object with officialNumber, name, imoNumber
 * @param {string} moduleName - Module name (equipment, drills, incidents, crew)
 * @param {number} yPosition - Current Y position on page
 * @returns {number} New Y position after adding metadata
 */
export function addComplianceMetadata(pdf, vessel, moduleName, yPosition) {
  const { margin } = getPageDimensions(pdf);
  let currentY = yPosition;

  // Add vessel Official Number (MCA requirement for UK-registered vessels)
  if (vessel.officialNumber) {
    setFont(pdf, 'body');
    pdf.setTextColor(51, 51, 51); // #333333
    pdf.text(`Official Number: ${vessel.officialNumber}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
  } else if (vessel.imoNumber) {
    // Fallback to IMO Number if Official Number not available
    setFont(pdf, 'body');
    pdf.setTextColor(51, 51, 51);
    pdf.text(`IMO Number: ${vessel.imoNumber}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
  }

  // Define retention periods and regulatory references per MCA/ISM requirements
  const retentionPeriods = {
    equipment: { years: 5, regulation: 'WBC3 3.5.2' },
    drills: { years: 2, regulation: 'MGN 71' },
    incidents: { years: 5, regulation: 'MSN 1853' },
    crew: { years: 2, regulation: 'MSN 1865' }
  };

  const retention = retentionPeriods[moduleName.toLowerCase()];
  if (retention) {
    // Calculate "Keep Until" date
    const keepUntilDate = new Date();
    keepUntilDate.setFullYear(keepUntilDate.getFullYear() + retention.years);
    const keepUntilStr = keepUntilDate.toISOString().split('T')[0];

    setFont(pdf, 'body');
    pdf.text(
      `Retention Period: ${retention.years} years (${retention.regulation}) - Keep Until: ${keepUntilStr}`,
      margin,
      currentY
    );
    currentY += PDF_CONFIG.lineHeight;
  }

  // Add MCA compliance notice
  setFont(pdf, 'small');
  pdf.setTextColor(102, 102, 102); // #666666
  pdf.text(
    'This document meets MCA recordkeeping requirements for UK-registered vessels',
    margin,
    currentY
  );
  pdf.setTextColor(51, 51, 51); // Reset to default
  currentY += 7;

  return currentY;
}

/**
 * Add hash chain verification status banner to PDF
 * Displays green banner for valid chains or red banner for broken chains
 *
 * @param {jsPDF} pdf - PDF document instance
 * @param {Array} auditEntries - Array of audit log entries with hash chains
 * @param {number} yPosition - Current Y position on page
 * @returns {Promise<number>} New Y position after adding banner
 */
export async function addHashVerificationBanner(pdf, auditEntries, yPosition) {
  // Import verification function from hashUtils.js (Days 16-17)
  const { verifyHashChain } = await import('../hashUtils.js');

  // Run chain verification
  const verification = await verifyHashChain(auditEntries);

  const { margin, contentWidth } = getPageDimensions(pdf);
  const bannerHeight = 10;
  let currentY = yPosition;

  if (verification.valid) {
    // Green banner for valid chain
    pdf.setFillColor(212, 237, 218); // #d4edda
    pdf.rect(margin, currentY, contentWidth, bannerHeight, 'F');

    setFont(pdf, 'body');
    pdf.setTextColor(21, 87, 36); // #155724
    pdf.text('✓ Hash Chain Valid - Audit Trail Verified', margin + 3, currentY + 6);
    pdf.setTextColor(51, 51, 51); // Reset to default
  } else {
    // Red banner for broken chain
    pdf.setFillColor(248, 215, 218); // #f8d7da
    pdf.rect(margin, currentY, contentWidth, bannerHeight, 'F');

    setFont(pdf, 'body');
    pdf.setTextColor(114, 28, 36); // #721c24
    const brokenText = verification.brokenAt !== null
      ? `✗ Hash Chain Broken at Entry ${verification.brokenAt + 1}`
      : '✗ Hash Chain Verification Failed';
    pdf.text(brokenText, margin + 3, currentY + 6);
    pdf.setTextColor(51, 51, 51); // Reset to default
  }

  currentY += bannerHeight + 3;
  return currentY;
}

/**
 * Add MCA inspection footer to PDF
 * Displays "Prepared for MCA Inspection" text, export timestamp, and page number
 *
 * @param {jsPDF} pdf - PDF document instance
 * @param {string} exportTimestamp - ISO timestamp of export
 * @param {number} pageNumber - Current page number
 * @param {number} totalPages - Total pages in document
 */
export function addMCAFooter(pdf, exportTimestamp, pageNumber, totalPages) {
  const { pageWidth, pageHeight, margin } = getPageDimensions(pdf);
  const footerY = pageHeight - 10;

  setFont(pdf, 'footer');
  pdf.setTextColor(102, 102, 102); // #666666

  // Left: MCA inspection notice
  pdf.text('Prepared for MCA Inspection', margin, footerY);

  // Center: Export timestamp in GB locale format
  const timestampText = `Exported: ${new Date(exportTimestamp).toLocaleString('en-GB')}`;
  const timestampWidth = pdf.getTextWidth(timestampText);
  pdf.text(timestampText, (pageWidth - timestampWidth) / 2, footerY);

  // Right: Page number
  const pageText = `Page ${pageNumber} of ${totalPages}`;
  const pageTextWidth = pdf.getTextWidth(pageText);
  pdf.text(pageText, pageWidth - margin - pageTextWidth, footerY);

  // Reset text color
  pdf.setTextColor(51, 51, 51);
}